import {
  Tag,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@jem-open/jem-ui';
import { Download } from 'lucide-react';
import { fullName, downloadErrorReport } from '../../utils/csvHelpers';
import type { ValidatedRow } from '../../types/bulkUpload';

interface ReviewStepProps {
  employerName: string;
  validRows: ValidatedRow[];
  errorRows: ValidatedRow[];
}

const truncate = (value: string | undefined, max = 30): string => {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max)}...` : value;
};

const EmptyCell = () => <em className="text-greyscale-text-caption">empty</em>;

const ReviewStep = ({ employerName, validRows, errorRows }: ReviewStepProps) => (
  <div className="flex flex-col gap-sm">
    <h3 className="text-greyscale-text-body">
      Review upload for <span className="font-bold text-secondary-surface">{employerName}</span>
    </h3>

    <div className="flex items-center gap-xxs">
      <Tag variant="success">{validRows.length} valid</Tag>
      {errorRows.length > 0 && (
        <Tag variant="failed">{errorRows.length} with errors</Tag>
      )}
    </div>

    {errorRows.length > 0 && (
      <div className="max-w-[1200px] w-full">
        <div className="flex items-center justify-between mb-xxs">
          <h6 className="text-sm font-semibold text-error-text-label">Rows with errors</h6>
          <span className="text-xs text-greyscale-text-caption italic">Showing key fields only — download report for full details</span>
        </div>
        <div className="max-h-[200px] overflow-y-auto rounded-lg border border-greyscale-border-default">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Row</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Errors</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {errorRows.map((r) => (
                <TableRow key={r.row}>
                  <TableCell>{r.row}</TableCell>
                  <TableCell>
                    {fullName(r.data) || <EmptyCell />}
                  </TableCell>
                  <TableCell>
                    {r.data.email || <EmptyCell />}
                  </TableCell>
                  <TableCell>
                    {r.data.mobile_number || <EmptyCell />}
                  </TableCell>
                  <TableCell className="text-error-text-label" title={r.errors.join('; ')}>
                    {truncate(r.errors.join('; '), 60)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end mt-xs">
          <Button
            variant="link"
            size="medium"
            leftIcon={<Download className="h-5 w-5" />}
            onClick={() => downloadErrorReport(errorRows)}
          >
            Download full error report
          </Button>
        </div>
      </div>
    )}

    {validRows.length === 0 && errorRows.length === 0 && (
      <p className="text-greyscale-text-caption italic">No data rows found in the uploaded CSV.</p>
    )}

    {validRows.length > 0 && (
      <div className="max-w-[1200px] w-full">
        <div className="flex items-center justify-between mb-xxs">
          <h6 className="text-sm font-semibold text-success-text-label">Valid rows to create</h6>
          <span className="text-xs text-greyscale-text-caption italic">Showing key fields only — all CSV columns will be submitted</span>
        </div>
        <div className="max-h-[300px] overflow-y-auto rounded-lg border border-greyscale-border-default">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Row</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Queries from Branches</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {validRows.map((r) => (
                <TableRow key={r.row}>
                  <TableCell>{r.row}</TableCell>
                  <TableCell>{fullName(r.data)}</TableCell>
                  <TableCell>{r.data.email}</TableCell>
                  <TableCell>{r.data.mobile_number}</TableCell>
                  <TableCell title={r.data.roles}>{truncate(r.data.roles) || <EmptyCell />}</TableCell>
                  <TableCell title={r.data.receive_queries_from_branches}>{truncate(r.data.receive_queries_from_branches) || <EmptyCell />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )}
  </div>
);

export default ReviewStep;
